import React, { useState, useEffect } from 'react';
import { auditsAPI, departmentsAPI, usersAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { Plus, Calendar as CalendarIcon, Edit, Trash2, PlayCircle, Loader2 } from 'lucide-react';
import { format } from 'date-fns';

const AuditPlanningPage = () => {
  const [audits, setAudits] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [auditors, setAuditors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingAudit, setEditingAudit] = useState(null);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    audit_name: '',
    audit_period: '',
    departments: [],
    auditor_id: '',
    audit_date: new Date(),
    status: 'Planned',
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [auditsRes, deptsRes, usersRes] = await Promise.all([
        auditsAPI.getAll(),
        departmentsAPI.getAll(),
        usersAPI.getAll().catch(() => ({ data: [] })),
      ]);
      setAudits(auditsRes.data);
      setDepartments(deptsRes.data);
      setAuditors(usersRes.data?.filter(u => u.role === 'Auditor') || []);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const payload = {
        ...formData,
        audit_date: format(formData.audit_date, 'yyyy-MM-dd'),
      };

      if (editingAudit) {
        await auditsAPI.update(editingAudit.id, payload);
        toast.success('Audit plan updated successfully');
      } else {
        await auditsAPI.create(payload);
        toast.success('Audit plan created successfully');
      }

      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save audit plan');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (audit) => {
    setEditingAudit(audit);
    setFormData({
      audit_name: audit.audit_name,
      audit_period: audit.audit_period,
      departments: audit.departments,
      auditor_id: audit.auditor_id,
      audit_date: new Date(audit.audit_date),
      status: audit.status,
    });
    setDialogOpen(true);
  };

  const handleDelete = async (auditId) => {
    if (!window.confirm('Are you sure you want to delete this audit plan?')) return;

    try {
      await auditsAPI.delete(auditId);
      toast.success('Audit plan deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete audit plan');
    }
  };

  const resetForm = () => {
    setEditingAudit(null);
    setFormData({
      audit_name: '',
      audit_period: '',
      departments: [],
      auditor_id: '',
      audit_date: new Date(),
      status: 'Planned',
    });
  };

  const toggleDepartment = (deptId) => {
    setFormData(prev => ({
      ...prev,
      departments: prev.departments.includes(deptId)
        ? prev.departments.filter(id => id !== deptId)
        : [...prev.departments, deptId],
    }));
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Completed': return 'badge-compliant';
      case 'In Progress': return 'badge-partial';
      default: return 'badge-pending';
    }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="audit-planning-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <p className="text-slate-600">Create and manage audit plans for ISO 27001 compliance</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) resetForm();
        }}>
          <DialogTrigger asChild>
            <Button className="bg-[#4A5690] hover:bg-[#3D477A] btn-scale" data-testid="create-audit-btn">
              <Plus size={18} className="mr-2" />
              Create Audit Plan
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
                {editingAudit ? 'Edit Audit Plan' : 'Create New Audit Plan'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label htmlFor="audit_name">Audit Name</Label>
                <Input
                  id="audit_name"
                  value={formData.audit_name}
                  onChange={(e) => setFormData({ ...formData, audit_name: e.target.value })}
                  placeholder="Q1 2024 ISO 27001 Internal Audit"
                  required
                  data-testid="audit-name-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="audit_period">Audit Period</Label>
                <Input
                  id="audit_period"
                  value={formData.audit_period}
                  onChange={(e) => setFormData({ ...formData, audit_period: e.target.value })}
                  placeholder="Jan 2024 - Mar 2024"
                  required
                  data-testid="audit-period-input"
                />
              </div>

              <div className="space-y-2">
                <Label>Departments</Label>
                <div className="border rounded-md p-3 max-h-40 overflow-y-auto space-y-2">
                  {departments.map((dept) => (
                    <div key={dept.id} className="flex items-center gap-2">
                      <Checkbox
                        id={`dept-${dept.id}`}
                        checked={formData.departments.includes(dept.id)}
                        onCheckedChange={() => toggleDepartment(dept.id)}
                        data-testid={`dept-checkbox-${dept.id}`}
                      />
                      <label htmlFor={`dept-${dept.id}`} className="text-sm cursor-pointer">
                        {dept.name}
                      </label>
                    </div>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="auditor">Auditor</Label>
                <Select
                  value={formData.auditor_id}
                  onValueChange={(value) => setFormData({ ...formData, auditor_id: value })}
                >
                  <SelectTrigger data-testid="auditor-select">
                    <SelectValue placeholder="Select auditor" />
                  </SelectTrigger>
                  <SelectContent>
                    {auditors.map((auditor) => (
                      <SelectItem key={auditor.id} value={auditor.id}>
                        {auditor.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Audit Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full justify-start text-left font-normal"
                      data-testid="audit-date-btn"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {format(formData.audit_date, 'PPP')}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={formData.audit_date}
                      onSelect={(date) => date && setFormData({ ...formData, audit_date: date })}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>

              {editingAudit && (
                <div className="space-y-2">
                  <Label htmlFor="status">Status</Label>
                  <Select
                    value={formData.status}
                    onValueChange={(value) => setFormData({ ...formData, status: value })}
                  >
                    <SelectTrigger data-testid="status-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Planned">Planned</SelectItem>
                      <SelectItem value="In Progress">In Progress</SelectItem>
                      <SelectItem value="Completed">Completed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setDialogOpen(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="flex-1 bg-[#4A5690] hover:bg-[#3D477A]"
                  disabled={saving}
                  data-testid="save-audit-btn"
                >
                  {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  {editingAudit ? 'Update' : 'Create'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Audits Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-slate-500">Loading audits...</div>
          ) : audits.length === 0 ? (
            <div className="p-8 text-center text-slate-500">
              <CalendarIcon size={40} className="mx-auto mb-3 text-slate-300" />
              <p>No audit plans created yet</p>
              <p className="text-sm">Click "Create Audit Plan" to get started</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Audit Name</th>
                    <th>Period</th>
                    <th>Departments</th>
                    <th>Auditor</th>
                    <th>Date</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {audits.map((audit) => (
                    <tr key={audit.id}>
                      <td className="font-medium text-slate-900">{audit.audit_name}</td>
                      <td>{audit.audit_period}</td>
                      <td>
                        <span className="text-sm text-slate-600">
                          {audit.departments?.length || 0} dept(s)
                        </span>
                      </td>
                      <td>{audit.auditor_name || 'Not assigned'}</td>
                      <td>{new Date(audit.audit_date).toLocaleDateString()}</td>
                      <td>
                        <Badge className={getStatusColor(audit.status)}>
                          {audit.status}
                        </Badge>
                      </td>
                      <td>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleEdit(audit)}
                            data-testid={`edit-audit-${audit.id}`}
                          >
                            <Edit size={16} />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDelete(audit.id)}
                            className="text-red-500 hover:text-red-600"
                            data-testid={`delete-audit-${audit.id}`}
                          >
                            <Trash2 size={16} />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AuditPlanningPage;
